import React, { useRef, useEffect, useCallback, useState, useLayoutEffect, memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { useSelectionStore } from '@/stores/selectionStore';
import { useCanvasStore } from '@/stores/canvasStore';
import { useShallow } from 'zustand/react/shallow';

interface MagneticHandleProps {
  type: 'target' | 'source';
  position: Position;
  nodeId: string;
  cardHeight?: number;
  cardWidth?: number;
}

const MagneticHandleInner: React.FC<MagneticHandleProps> = ({
  type,
  position,
  nodeId,
  cardHeight = 236,
  cardWidth = 420,
}) => {
  const canvasZoomRef = useRef(useCanvasStore.getState().zoom);
  const handleRef = useRef<HTMLDivElement>(null);
  const originalCenterRef = useRef<{ x: number; y: number } | null>(null);
  const isSnappedRef = useRef(false);
  const [isSnapped, setIsSnapped] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [snapOffset, setSnapOffset] = useState({ x: 0, y: 0 });

  const handleId = `${nodeId}-${type}-${position}`;

  const {
    mouseX,
    mouseY,
    activeHandleId,
    isConnecting,
    setMagneticZoom,
    registerHandle,
    unregisterHandle,
    setActiveHandle,
    setConnecting,
    initGlobalListener,
  } = useSelectionStore(
    useShallow(s => {
      if (s.activeHandleId !== handleId) {
        return {
          mouseX: 0, mouseY: 0,
          activeHandleId: s.activeHandleId,
          isConnecting: s.isConnecting,
          setMagneticZoom: s.setMagneticZoom,
          registerHandle: s.registerHandle,
          unregisterHandle: s.unregisterHandle,
          setActiveHandle: s.setActiveHandle,
          setConnecting: s.setConnecting,
          initGlobalListener: s.initGlobalListener,
        };
      }
      return {
        mouseX: s.mouseX, mouseY: s.mouseY,
        activeHandleId: s.activeHandleId,
        isConnecting: s.isConnecting,
        setMagneticZoom: s.setMagneticZoom,
        registerHandle: s.registerHandle,
        unregisterHandle: s.unregisterHandle,
        setActiveHandle: s.setActiveHandle,
        setConnecting: s.setConnecting,
        initGlobalListener: s.initGlobalListener,
      };
    })
  );

  const updatePosition = useCallback(() => {
    const handleEl = handleRef.current;
    if (!handleEl) return;
    if (isSnappedRef.current) return;

    const rect = handleEl.getBoundingClientRect();
    const currentX = rect.left + rect.width / 2;
    const currentY = rect.top + rect.height / 2;

    originalCenterRef.current = { x: currentX, y: currentY };
    registerHandle(handleId, currentX, currentY);
  }, [handleId, registerHandle]);

  useEffect(() => {
    // P0: 只在 zoom 变化时更新，x/y 变化（平移）不需要重新计算 handle 位置
    // handle 位置相对于节点，不随画布平移变化
    // 之前无过滤条件，中键平移时 2N 个 handle 都调用 getBoundingClientRect() 导致布局抖动
    let prevZoom = useCanvasStore.getState().zoom;
    const unsub = useCanvasStore.subscribe((state) => {
      if (state.zoom === prevZoom) return;
      prevZoom = state.zoom;
      canvasZoomRef.current = state.zoom;
      setMagneticZoom(state.zoom);
      updatePosition();
    });
    setMagneticZoom(useCanvasStore.getState().zoom);
    return unsub;
  }, [setMagneticZoom, updatePosition]);

  useEffect(() => {
    initGlobalListener();
  }, [initGlobalListener]);

  useLayoutEffect(() => {
    updatePosition();
  }, [cardHeight, cardWidth, updatePosition]);

  useEffect(() => {
    registerHandle(handleId, 0, 0);
    return () => {
      unregisterHandle(handleId);
    };
  }, [handleId, registerHandle, unregisterHandle]);

  useEffect(() => {
    const isActive = activeHandleId === handleId;
    const canvasZoom = canvasZoomRef.current;

    if (isActive && !isConnecting) {
      const center = originalCenterRef.current;
      if (!center) {
        updatePosition();
        return;
      }

      isSnappedRef.current = true;
      setIsSnapped(true);
      setIsHovered(true);

      const maxOffset = 20 * canvasZoom;
      const dx = mouseX - center.x;
      const dy = mouseY - center.y;
      const clampedX = Math.max(-maxOffset, Math.min(maxOffset, dx * 0.5));
      const clampedY = Math.max(-maxOffset, Math.min(maxOffset, dy * 0.5));
      setSnapOffset({ x: clampedX, y: clampedY });
    } else if (isActive) {
      setIsHovered(true);
      setIsSnapped(false);
      isSnappedRef.current = false;
      setSnapOffset({ x: 0, y: 0 });
      updatePosition();
    } else {
      setIsHovered(false);
      setIsSnapped(false);
      isSnappedRef.current = false;
      setSnapOffset({ x: 0, y: 0 });
      updatePosition();
    }
  }, [mouseX, mouseY, activeHandleId, isConnecting, handleId, updatePosition]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setConnecting(true);
    setIsSnapped(true);
    isSnappedRef.current = true;
  }, [setConnecting]);

  const handleMouseUp = useCallback(() => {
    setConnecting(false);
    setIsSnapped(false);
    isSnappedRef.current = false;
    setSnapOffset({ x: 0, y: 0 });
    setActiveHandle(null);
    updatePosition();
  }, [setConnecting, setActiveHandle, updatePosition]);

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseUp]);

  const baseSize = 20;
  const hoverScale = 1.25;
  const snappedScale = 1.35;

  const currentScale = isSnapped ? snappedScale : (isHovered ? hoverScale : 1);

  const isLeft = position === Position.Left;
  const isRight = position === Position.Right;

  return (
    <div
      className={`absolute z-30 ${isLeft ? 'left-0' : isRight ? 'right-0' : ''}`}
      style={{
        top: '50%',
        transform: `translateY(-50%) translate(${snapOffset.x / canvasZoomRef.current}px, ${snapOffset.y / canvasZoomRef.current}px)`,
        transition: isSnapped ? 'none' : 'transform 0.15s ease-out',
      }}
      onMouseDown={handleMouseDown}
    >
      <div
        ref={handleRef}
        style={{
          position: 'absolute',
          width: baseSize,
          height: baseSize,
          left: isLeft ? -baseSize / 2 : undefined,
          right: isRight ? -baseSize / 2 : undefined,
          top: '50%',
          transform: `translateY(-50%) scale(${currentScale})`,
        }}
      >
        <Handle
          type={type}
          position={position}
          id={handleId}
          className={`!rounded-full !transition-all !duration-150 !cursor-default !p-0 ${
            isSnapped
              ? '!bg-white !border-2 !border-white !shadow-[0_0_12px_rgba(34,211,238,0.5)]'
              : isHovered
                ? '!bg-[#1c1c1e] !border !border-white/50'
                : '!bg-[#1c1c1e] !border !border-white/20'
          }`}
          style={{
            width: baseSize,
            height: baseSize,
            left: 0,
            right: 0,
          }}
        >
          <div className="w-full h-full flex items-center justify-center pointer-events-none">
            <svg
              width={baseSize * 0.4}
              height={baseSize * 0.4}
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M7 3V11M3 7H11"
                stroke={isSnapped ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.5)'}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </Handle>
      </div>
    </div>
  );
};

export const MagneticHandle = memo(MagneticHandleInner, (prevProps, nextProps) => {
  return (
    prevProps.type === nextProps.type &&
    prevProps.position === nextProps.position &&
    prevProps.nodeId === nextProps.nodeId &&
    prevProps.cardHeight === nextProps.cardHeight &&
    prevProps.cardWidth === nextProps.cardWidth
  );
});
